import { ReactNode, useCallback, useState } from 'react';

import { ConfirmationModalTranslations, ExerciseModalTranslations, LtiExerciseMaterial } from './types';
import Confirmation from './Confirmation';
import StartLTIExercise from './StartLTIExercise';
import { Module } from '../../util/buildBookWidgetsUrl';
import { TokenAccessor } from '../../types/API';
import { isScorableLti } from './utils';
import { AnalyticsData } from '../../hooks/constants/Analytics';
import { Platform } from '../lti-modal';
import PillGroup from '@pelckmans/elementis/components/pill-group';

function getInitialPhase(material: LtiExerciseMaterial): 'confirmation' | 'start' {
  if (isScorableLti(material)) return 'confirmation';

  return 'start';
}

export type ModalProps = {
  id?: string;
  material: LtiExerciseMaterial;
  tokenAccessor: TokenAccessor;
  onClose: () => void;
  translations: {
    confirmationModals: ConfirmationModalTranslations;
    exerciseModal: ExerciseModalTranslations;
  };
  module: Module;
  platformName: Platform;
  ltipsUrl: string;
  studioLanguage: string;
  isOwner: boolean;
  analyticsData?: AnalyticsData;
  studioGroup: string;
  tags: ReactNode;
};

export function LtiExerciseModal({
  id = 'lti-exercise-modal',
  material,
  tokenAccessor,
  onClose,
  translations,
  module,
  platformName,
  ltipsUrl,
  studioLanguage,
  isOwner,
  analyticsData,
  studioGroup,
  tags,
}: ModalProps): JSX.Element {
  const [phase, setPhase] = useState(getInitialPhase(material));

  const onConfirm = useCallback(() => setPhase('start'), []);

  if (phase === 'confirmation' && isScorableLti(material)) {
    return (
      <Confirmation
        id={id}
        material={material}
        onConfirm={onConfirm}
        tokenAccessor={tokenAccessor}
        translations={translations.confirmationModals}
        isOwner={isOwner}
        studioGroup={studioGroup}
        tags={tags}
      />
    );
  }

  return (
    <StartLTIExercise
      id={id}
      onClose={onClose}
      headerProps={{
        title: material.name,
        titleTooltip: material.name,
        tags: (
          <PillGroup role={undefined} aria-label={undefined}>
            {tags}
          </PillGroup>
        ),
      }}
      tokenAccessor={tokenAccessor}
      translations={translations.exerciseModal}
      material={material}
      module={module}
      platformName={platformName}
      ltipsUrl={ltipsUrl}
      studioLanguage={studioLanguage}
      isOwner={isOwner}
      analyticsData={analyticsData}
      studioGroup={studioGroup}
    />
  );
}
